/**
 * Custom React Hook for WebSocket connection to receive real-time order updates
 * Replaces polling with instant push notifications from backend
 */

import { useCallback, useEffect, useMemo, useRef, useState, startTransition } from 'react';
import { WS_ZIPPY_URL } from '../config/api.config';
import logger from '../utils/logger';

interface Order {
  id: number;
  type: string;
  stock: string;
  hedge: string;
  hedgeStrike: number;
  strike: number;
  otype: string;
  trend: string;
  entryLogic: string;
  logicCondition: string;
  logicSpotPrice: number;
  orderSide: string;
  qtySize: number;
  limitPrice: number;
  orderType: string;
  productType: string;
  slCheck: string;
  slPrice: number;
  tgCheck: string;
  tgPrice: number;
  status: string;
  exitType?: string;
  created_at: string;
  updated_at: string | null;
}

interface OrderBookItem {
  uid: number;
  guid: string;
  order_id: string;
  order_unique_identifier: string;
  client_id: string;
  instrument_identifier: string;
  exchange_instrument_id: number;
  atp: number;
  trade_type: string;
  order_status: string;
  order_side: string;
  order_generated_time: string;
  exchange_transact_time: string;
  quantity: number;
  limit_price: number;
  stop_price: number;
  order_type: string;
  last_updated_time: string;
  pdt_type: string;
  order_reject_reason: string;
  stock: string;
  order_date: string;
  lot_size: number;
  is_order_completed: boolean;
  is_order_placed: boolean;
  exchange_transaction_orderid: string;
  exchange_segment: string;
  generated_by: string;
  order_added_time: string;
  order_update_source: string;
  retry_count: number;
  broker_name: string;
  position_update: boolean;
}

interface PositionItem {
  uid: number;
  instrument: string;
  instrument_id: number;
  buy_price: number | null;
  buy_qty: number | null;
  sell_price: number | null;
  sell_qty: number | null;
  net_qty: number | null;
  ltp: number | null;
  gross_pnl: number | null;
  order_list: any; // JSON field
  created_at: string;
  updated_at: string;
}

interface WebSocketMessage {
  type: 'all_orders' | 'order_created' | 'order_updated' | 'order_deleted' | 'order_executed' | 'order_failed' | 'order_queued' | 'orderbook_all' | 'orderbook_created' | 'orderbook_updated' | 'position_all' | 'position_created' | 'position_updated' | 'mtm_snapshot' | 'mtm_ack' | 'margin_updated' | 'zippy_market_data' | 'zippy_market_data_batch' | 'market_data' | 'ping' | 'connected';
  data: any;
  margin_data?: any;
  timestamp: string;
  count?: number;
}

interface UseWebSocketOrdersReturn {
  orders: Order[];
  orderBook: OrderBookItem[];
  positions: PositionItem[];
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;
  sendJson?: (msg: any) => void;
  liveLTPMap: Map<number, number>;
  onMarketData: React.MutableRefObject<((message: any) => void) | null>;
}

/**
 * Format order for notification display
 * Format: Zippy PO - SYN CE | NIFTY 25100 | SL - "25050" | TG - "25150" | HEDGE
 */
const formatOrderNotification = (order: Order, includeStatus: boolean = false): string => {
  const parts: string[] = [];

  // Type and Option Type
  parts.push(`${order.type} ${order.otype}`);

  // Stock and Strike
  parts.push(`${order.stock?.toUpperCase()} ${order.strike}`);

  // Stop Loss
  if (order.slCheck === 'Yes' && order.slPrice && order.slPrice > 0) {
    parts.push(`SL - "${order.slPrice}"`);
  } else {
    parts.push(`No SL`);
  }

  // Target
  if (order.tgCheck === 'Yes' && order.tgPrice && order.tgPrice > 0) {
    parts.push(`TG - "${order.tgPrice}"`);
  } else {
    parts.push(`No TG`);
  }

  // Hedge (only if Yes)
  if (order.hedge === 'Yes') {
    parts.push('HEDGE');
  }

  let message = `Zippy PO - ${parts.join(' | ')}`;

  // Add status information if requested
  if (includeStatus) {
    message += ` --- Order Saved with UID - ${order.id} with Status ${order.status}`;
  }

  return message;
};

export const useWebSocketOrders = (url: string = WS_ZIPPY_URL): UseWebSocketOrdersReturn => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderBook, setOrderBook] = useState<OrderBookItem[]>([]);
  // Initialize positions with cached data for instant display on page reload
  const [positions, setPositions] = useState<PositionItem[]>(() => {
    try {
      const cached = localStorage.getItem('positionsDataCache');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep a Map of orders by ID for more reliable deduplication
  const ordersMapRef = useRef<Map<number, Order>>(new Map());

  // Keep a Map of order book items by UID for deduplication
  const orderBookMapRef = useRef<Map<number, OrderBookItem>>(new Map());

  // Keep a Map of positions by UID for deduplication
  const positionsMapRef = useRef<Map<number, PositionItem>>(new Map());

  // Track recent execution messages to prevent duplicate notifications
  // Key format: "orderId-timestamp" to allow same order to be executed multiple times
  const recentExecutionsRef = useRef<Set<string>>(new Set());
  // Track setTimeout IDs for cleanup on unmount
  const recentExecutionTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Live LTP map: instrument_id → latest price (fed by market data ticks)
  const [liveLTPMap, setLiveLTPMap] = useState<Map<number, number>>(new Map());
  const liveLTPMapRef = useRef<Map<number, number>>(new Map());
  const ltpRafRef = useRef<number | null>(null);

  // Callback ref for market data — MarketDataContext attaches its handler here
  const onMarketDataRef = useRef<((message: any) => void) | null>(null);

  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectDelay = 30000; // Max 30 seconds between reconnection attempts
  const isUnmounting = useRef(false); // Track if component is unmounting

  // RAF-throttled flush of liveLTPMap ref → state (caps at screen refresh rate)
  const scheduleRafFlush = useCallback(() => {
    if (ltpRafRef.current !== null) return; // Already scheduled
    ltpRafRef.current = requestAnimationFrame(() => {
      ltpRafRef.current = null;
      setLiveLTPMap(new Map(liveLTPMapRef.current));
    });
  }, []);

  // Helper: track a recentExecutions cleanup timer so it can be cancelled on unmount
  const scheduleExecutionCleanup = useCallback((key: string) => {
    const timer = setTimeout(() => {
      recentExecutionsRef.current.delete(key);
      recentExecutionTimersRef.current.delete(timer);
    }, 5000);
    recentExecutionTimersRef.current.add(timer);
  }, []);

  const connect = useCallback(() => {
    try {
      // Get authentication token and append to WebSocket URL
      const token = localStorage.getItem('access_token');
      const wsUrl = token ? `${url}?token=${token}` : url;

      logger.info('SOCKET', `Attempting WebSocket connection to ${url}`);
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        const connectionId = Math.random().toString(36).substr(2, 9);
        (ws.current as any)._connectionId = connectionId;
        logger.info('SOCKET', `WebSocket connected successfully`, { url, connectionId });
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0; // Reset attempts on successful connection
      };

      ws.current.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          // Handle ping/pong for connection health check
          if (message.type === 'ping') {
            // Respond with pong (optional - just receiving ping is enough)
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
              ws.current.send(JSON.stringify({ type: 'pong' }));
            }
            return; // Don't process further
          }

          // Handle initial connection message
          if (message.type === 'connected') {
            return; // Don't process further
          }

          switch (message.type) {
            case 'all_orders':
              // Initial full order list
              // Clear and rebuild the orders map
              ordersMapRef.current.clear();
              message.data.forEach((order: Order) => {
                ordersMapRef.current.set(order.id, order);
              });

              // Convert back to array and check for duplicates
              const uniqueOrders = Array.from(ordersMapRef.current.values());
              if (uniqueOrders.length !== message.data.length) {
                const dupeCount = message.data.length - uniqueOrders.length;
                console.error(`🚨 Removed ${dupeCount} duplicates from all_orders`);
                logger.warn('SOCKET', `Removed ${dupeCount} duplicate orders from initial data`);
              }

              // Don't log "Received initial order list" notification
              startTransition(() => setOrders(uniqueOrders));
              break;

            case 'order_created':
              // Add new order using Map for reliable deduplication
              const orderId = message.data.id;

              if (!ordersMapRef.current.has(orderId)) {
                ordersMapRef.current.set(orderId, message.data);
                setOrders(Array.from(ordersMapRef.current.values()));

                // Show formatted notification for new order (SUCCESS level only)
                const createNotification = formatOrderNotification(message.data, true);
                logger.success('ORDER', createNotification, { orderId: message.data.id });
              } else {
                // Order already exists - this is a duplicate WebSocket message
                logger.debug('SOCKET', `Skipped duplicate order_created`, { orderId });
              }
              break;

            case 'order_updated':
              // Update existing order using Map
              const updateOrderId = message.data.id;
              const previousOrder = ordersMapRef.current.get(updateOrderId);

              ordersMapRef.current.set(updateOrderId, message.data);
              setOrders(Array.from(ordersMapRef.current.values()));

              // Show notification about what changed
              if (previousOrder) {
                // Check if status changed
                if (previousOrder.status !== message.data.status) {
                  const statusNotification = formatOrderNotification(message.data, true);
                  logger.success('ORDER', statusNotification, { orderId: updateOrderId });
                } else {
                  // Check what fields changed
                  const changes: string[] = [];
                  if (previousOrder.slPrice !== message.data.slPrice) {
                    changes.push(`SL updated to ${message.data.slPrice}`);
                  }
                  if (previousOrder.tgPrice !== message.data.tgPrice) {
                    changes.push(`TG updated to ${message.data.tgPrice}`);
                  }
                  if (previousOrder.qtySize !== message.data.qtySize) {
                    changes.push(`Qty updated to ${message.data.qtySize}`);
                  }

                  if (changes.length > 0) {
                    logger.success('ORDER', `UID ${updateOrderId} - ${changes.join(', ')}`, { orderId: updateOrderId });
                  }
                }
              } else {
                // First time seeing this order (shouldn't happen but handle gracefully)
                const updateNotification = formatOrderNotification(message.data, true);
                logger.success('ORDER', updateNotification, { orderId: updateOrderId });
              }
              break;

            case 'order_deleted':
              // Remove order using Map
              const deleteOrderId = message.data.id;
              ordersMapRef.current.delete(deleteOrderId);
              setOrders(Array.from(ordersMapRef.current.values()));
              logger.info('ORDER', `UID ${deleteOrderId} - Order deleted`, { orderId: deleteOrderId });
              break;

            case 'order_executed':
              // Handle order execution success
              const executionData = message.data;
              const executedOrderId = executionData.AppOrderID || executionData.orderId || 'Unknown';
              const orderIdentifier = executionData.OrderUniqueIdentifier || executionData.orderUniqueIdentifier || '';

              // Create unique key for deduplication (order ID + timestamp)
              // This prevents duplicate messages while allowing same order to be executed multiple times
              const executionKey = `${executedOrderId}-${message.timestamp}`;

              // Check if we've already processed this exact execution message
              if (recentExecutionsRef.current.has(executionKey)) {
                logger.debug('SOCKET', `Skipped duplicate order_executed`, { orderId: executedOrderId });
                break;
              }

              // Mark this execution as processed
              recentExecutionsRef.current.add(executionKey);
              scheduleExecutionCleanup(executionKey);

              let executionMessage = `Order executed successfully! Order ID: ${executedOrderId}`;
              if (orderIdentifier) {
                executionMessage += ` (${orderIdentifier})`;
              }

              logger.success('ORDER', executionMessage, {
                appOrderId: executedOrderId,
                orderUniqueIdentifier: orderIdentifier,
                executionDetails: executionData
              });

              // Dispatch custom event for snackbar notification
              window.dispatchEvent(new CustomEvent('order_executed', {
                detail: { message: executionMessage, data: executionData }
              }));
              break;

            case 'order_failed':
              // Handle order failure
              const failureData = message.data;
              const failedOrderId = failureData.orderId || failureData.AppOrderID || 'Unknown';
              const failureReason = failureData.reason || 'Unknown error';

              // Create unique key for deduplication (order ID/reason + timestamp)
              const failureKey = `${failedOrderId}-${failureReason}-${message.timestamp}`;

              // Check if we've already processed this exact failure message
              if (recentExecutionsRef.current.has(failureKey)) {
                logger.debug('SOCKET', `Skipped duplicate order_failed`, { reason: failureReason });
                break;
              }

              // Mark this failure as processed
              recentExecutionsRef.current.add(failureKey);
              scheduleExecutionCleanup(failureKey);

              const failureMessage = `Order failed: ${failureReason}`;

              logger.error('ORDER', failureMessage, {
                reason: failureReason,
                failureDetails: message.data
              });

              // Dispatch custom event for snackbar notification
              window.dispatchEvent(new CustomEvent('order_failed', {
                detail: { message: failureMessage, reason: failureReason, data: message.data }
              }));
              break;

            case 'order_queued':
              // Handle order queued notification
              // data is a string message directly
              const queuedMessage = typeof message.data === 'string' ? message.data : message.data.message || 'Order queued successfully';

              // Create unique key for deduplication (message + timestamp)
              const queuedKey = `queued-${queuedMessage}-${message.timestamp}`;

              // Check if we've already processed this exact queued message
              if (recentExecutionsRef.current.has(queuedKey)) {
                logger.debug('SOCKET', `Skipped duplicate order_queued`, { message: queuedMessage });
                break;
              }

              // Mark this queued notification as processed
              recentExecutionsRef.current.add(queuedKey);
              scheduleExecutionCleanup(queuedKey);

              logger.info('ORDER', queuedMessage, {
                queuedDetails: message.data
              });

              // Dispatch custom event for snackbar notification
              window.dispatchEvent(new CustomEvent('order_queued', {
                detail: { message: queuedMessage, data: message.data }
              }));
              break;

            case 'orderbook_all':
              // Initial full order book list
              logger.debug('SOCKET', `Received orderbook_all`, { count: message.data.length });
              orderBookMapRef.current.clear();
              message.data.forEach((item: OrderBookItem) => {
                orderBookMapRef.current.set(item.uid, item);
              });

              const uniqueOrderBookItems = Array.from(orderBookMapRef.current.values());
              startTransition(() => setOrderBook(uniqueOrderBookItems));
              // Removed logger.info to prevent activity log spam
              break;

            case 'orderbook_created':
              // New order book item
              const newItem = message.data;

              if (!orderBookMapRef.current.has(newItem.uid)) {
                orderBookMapRef.current.set(newItem.uid, newItem);
                setOrderBook(Array.from(orderBookMapRef.current.values()));
                logger.info('ORDERBOOK', `New order book item - UID: ${newItem.uid}, Symbol: ${newItem.instrument_identifier}`);
              } else {
                logger.debug('ORDERBOOK', `Duplicate orderbook_created`, { uid: newItem.uid });
              }
              break;

            case 'orderbook_updated':
              // Update existing order book item
              const updatedItem = message.data;

              orderBookMapRef.current.set(updatedItem.uid, updatedItem);
              setOrderBook(Array.from(orderBookMapRef.current.values()));
              logger.info('ORDERBOOK', `Updated order book item - UID: ${updatedItem.uid}, Status: ${updatedItem.order_status}`);
              break;

            case 'position_all':
              // Initial full positions list
              logger.debug('SOCKET', `Received position_all`, { count: message.data.length });
              positionsMapRef.current.clear();
              message.data.forEach((item: PositionItem) => {
                positionsMapRef.current.set(item.uid, item);
              });

              const uniquePositions = Array.from(positionsMapRef.current.values());
              startTransition(() => setPositions(uniquePositions));

              // Cache positions to localStorage for instant display on page reload
              try {
                localStorage.setItem('positionsDataCache', JSON.stringify(uniquePositions));
              } catch (e) {
                console.error('Failed to cache positions data:', e);
              }
              break;

            case 'position_created':
              // New position item
              const newPosition = message.data;

              if (!positionsMapRef.current.has(newPosition.uid)) {
                positionsMapRef.current.set(newPosition.uid, newPosition);
                const updatedPositions = Array.from(positionsMapRef.current.values());
                setPositions(updatedPositions);

                // Cache updated positions
                try {
                  localStorage.setItem('positionsDataCache', JSON.stringify(updatedPositions));
                } catch (e) {
                  console.error('Failed to cache positions data:', e);
                }
              }
              break;

            case 'position_updated':
              // Update existing position
              const updatedPosition = message.data;

              if (updatedPosition.uid != null) {
                positionsMapRef.current.set(updatedPosition.uid, updatedPosition);
              } else {
                // Fallback: match by instrument_id when uid is missing
                const existingEntry = Array.from(positionsMapRef.current.entries())
                  .find(([, p]) => p.instrument_id === updatedPosition.instrument_id);
                if (existingEntry) {
                  const merged = { ...existingEntry[1], ...updatedPosition };
                  positionsMapRef.current.set(existingEntry[0], merged);
                }
                // Skip if no matching position found (wait for position_created with uid)
                if (!existingEntry) break;
              }
              const latestPositions = Array.from(positionsMapRef.current.values());
              setPositions(latestPositions);

              // Cache updated positions
              try {
                localStorage.setItem('positionsDataCache', JSON.stringify(latestPositions));
              } catch (e) {
                console.error('Failed to cache positions data:', e);
              }
              break;

            case 'mtm_snapshot':
              // Real-time MTM snapshot push from backend
              if ((window as any).__zippy_mtm_chart_open === true) {
                window.dispatchEvent(new CustomEvent('mtm_snapshot', {
                  detail: message.data
                }));
              }
              break;

            case 'mtm_ack':
              // Ack for sender tab; no UI action needed here
              break;

            case 'margin_updated':
              window.dispatchEvent(new CustomEvent('margin_updated', {
                detail: { margin_data: message.margin_data }
              }));
              break;

            case 'zippy_market_data': {
              // Single instrument tick — update LTP map and forward to MarketDataContext
              const { xID, LTP } = message.data;
              if (xID && LTP !== undefined) {
                liveLTPMapRef.current.set(xID, LTP);
                scheduleRafFlush();
              }
              // Forward to MarketDataContext handler
              if (onMarketDataRef.current) {
                onMarketDataRef.current(message);
              }
              break;
            }

            case 'zippy_market_data_batch': {
              // Batched ticks — array of {xID, LTP, Time, Change}
              const ticks: any[] = message.data;
              for (const tick of ticks) {
                if (tick.xID && tick.LTP !== undefined) {
                  liveLTPMapRef.current.set(tick.xID, tick.LTP);
                }
                // Forward individual ticks to MarketDataContext
                if (onMarketDataRef.current) {
                  onMarketDataRef.current({ type: 'zippy_market_data', data: tick, timestamp: message.timestamp });
                }
              }
              scheduleRafFlush();
              break;
            }

            case 'market_data': {
              // Legacy format — forward to MarketDataContext only
              if (onMarketDataRef.current) {
                onMarketDataRef.current(message);
              }
              break;
            }

            default:
              break;
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
          logger.error('SOCKET', `Error parsing WebSocket message`, { error: String(err) });
        }
      };

      ws.current.onerror = (event) => {
        console.error('❌ WebSocket Error:', event);
        logger.error('SOCKET', `WebSocket connection error`, { event: String(event) });
        setError('WebSocket connection error');
      };

      ws.current.onclose = () => {
        setIsConnected(false);
        logger.warn('SOCKET', `WebSocket connection closed`);

        // Don't reconnect if component is unmounting or being cleaned up
        if (isUnmounting.current) {
          logger.info('SOCKET', `Component unmounting - will not reconnect`);
          return;
        }

        // Infinite reconnection with exponential backoff (capped at 30 seconds)
        // This ensures the frontend will automatically connect when backend starts
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), maxReconnectDelay);

        logger.info('SOCKET', `Attempting reconnection`, {
          attempt: reconnectAttempts.current + 1,
          delaySeconds: delay / 1000
        });

        reconnectTimeout.current = setTimeout(() => {
          reconnectAttempts.current++;
          connect();
        }, delay);
      };
    } catch (err) {
      logger.error('SOCKET', `Failed to create WebSocket connection`, { error: String(err) });
      console.error('Error creating WebSocket:', err);
      setError('Failed to create WebSocket connection');
    }
  }, [url, scheduleRafFlush, scheduleExecutionCleanup]);

  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    if (ws.current) {
      ws.current.close();
    }
    connect();
  }, [connect]);

  const sendJson = useCallback((msg: any) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      try {
        ws.current.send(JSON.stringify(msg));
      } catch (e) {
        logger.error('SOCKET', 'Failed to send WS message', { error: String(e) });
      }
    }
  }, []);

  useEffect(() => {
    isUnmounting.current = false; // Reset on mount
    connect();

    const timersRef = recentExecutionTimersRef.current;

    // Cleanup on unmount or hot reload
    return () => {
      isUnmounting.current = true; // Prevent reconnect attempts

      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }

      // Cancel pending RAF flush
      if (ltpRafRef.current !== null) {
        cancelAnimationFrame(ltpRafRef.current);
        ltpRafRef.current = null;
      }

      // Cancel all recentExecutions cleanup timers
      for (const timer of timersRef) {
        clearTimeout(timer);
      }
      timersRef.clear();

      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.close(1000, 'Component unmounting'); // Normal closure
      }
    };
  }, [connect]);

  // PERFORMANCE OPTIMIZATION: Memoize return object to prevent unnecessary re-renders
  // When WebSocketContext spreads this object, a stable reference prevents context consumers from re-rendering
  return useMemo(() => ({
    orders,
    orderBook,
    positions,
    isConnected,
    error,
    reconnect,
    sendJson,
    liveLTPMap,
    onMarketData: onMarketDataRef,
  }), [orders, orderBook, positions, isConnected, error, reconnect, sendJson, liveLTPMap]);
};

export default useWebSocketOrders;
